"use client";

import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { Assignment, Review, Submission } from "@/types";

function formatDeadline(isoString: string): string {
  const d = new Date(isoString);
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "Europe/London" });
  const day = d.toLocaleDateString("en-GB", { day: "numeric", timeZone: "Europe/London" });
  const month = d.toLocaleDateString("en-GB", { month: "short", timeZone: "Europe/London" });
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
    hour12: false,
  });
  return `${weekday} ${day} ${month}, ${time}`;
}

// Diagonal hatch fills matching the reference design
const HATCH_PLACEHOLDER =
  "repeating-linear-gradient(45deg, rgba(60,84,74,.12) 0 3px, rgba(60,84,74,.03) 3px 6px)";
const HATCH_IN_REVIEW =
  "repeating-linear-gradient(45deg, rgba(30,111,79,.28) 0 3px, rgba(30,111,79,.06) 3px 6px)";
const HATCH_DROP = "repeating-linear-gradient(45deg, #B0563C 0 3px, #8A3F2B 3px 6px)";

// Deterministic heights for the not-yet-open placeholder bars. Indexed by
// work-package position modulo its length so any cohort size gets stable bars.
const PLACEHOLDER_HEIGHTS = [55, 68, 62, 71, 46, 60, 75, 50];

type BarState = "scored" | "in_review" | "placeholder";

interface BarData {
  label: string;
  state: BarState;
  score: number | null;
  // Score of the first submission when a revision changed it (not yet
  // exposed by the API — caps and deltas render once it is)
  firstScore: number | null;
  placeholderHeight: number;
}

function ScoreBar({ bar }: { bar: BarData }) {
  const delta = bar.score !== null && bar.firstScore !== null ? bar.score - bar.firstScore : null;
  const capPct = delta === null ? 0 : Math.abs(delta);
  const totalPct =
    bar.state === "scored"
      ? Math.max(bar.score ?? 0, bar.firstScore ?? bar.score ?? 0)
      : bar.placeholderHeight;
  // Vertical space not available to bars: value row (24px) + x-axis labels (26px).
  // The height goes on the bar stack itself — children fill it proportionally.
  const stackHeight = `calc((100% - 50px) * ${totalPct / 100})`;

  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-end">
      <div className="mb-1 flex h-5 items-center gap-1">
        {bar.state === "scored" && (
          <>
            <span className="text-[13px] font-bold text-content-foreground">{bar.score}</span>
            {delta !== null && delta !== 0 && (
              <span
                className={`text-[11px] font-bold ${delta < 0 ? "text-[#B0563C]" : "text-[#1E6F4F]"}`}
              >
                {delta < 0 ? "▼" : "▲"}
                {Math.abs(delta)}
              </span>
            )}
          </>
        )}
        {bar.state === "in_review" && (
          <span className="whitespace-nowrap font-serif text-[12.5px] italic text-content-muted">
            in review
          </span>
        )}
      </div>
      <div className="flex w-full max-w-[42px] flex-col" style={{ height: stackHeight }}>
        {bar.state === "scored" && capPct > 0 && (
          <div
            className="rounded-t-[7px]"
            style={{
              height: `${(capPct / Math.max(totalPct, 1)) * 100}%`,
              background: delta !== null && delta < 0 ? HATCH_DROP : "#9FDDBC",
            }}
          />
        )}
        {bar.state === "scored" && (
          <div className={capPct > 0 ? "flex-1 bg-brand" : "flex-1 rounded-t-[7px] bg-brand"} />
        )}
        {bar.state === "in_review" && (
          <div
            className="flex-1 rounded-t-[7px] border border-dashed border-brand"
            style={{ background: HATCH_IN_REVIEW }}
          />
        )}
        {bar.state === "placeholder" && (
          <div className="flex-1 rounded-t-[7px]" style={{ background: HATCH_PLACEHOLDER }} />
        )}
      </div>
      <div className="flex h-[26px] items-end pb-0.5 text-[11px] font-bold tracking-[.08em] text-content-muted/80">
        {bar.label}
      </div>
    </div>
  );
}

interface ReportScoreChartProps {
  assignments: Assignment[];
  submissions: Submission[];
  reviews: Review[];
}

export function ReportScoreChart({ assignments, submissions, reviews }: ReportScoreChartProps) {
  if (!assignments || assignments.length === 0) {
    return (
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand to-green-700/0" />
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-content-muted uppercase tracking-[2px]">
                Feedback
              </p>
              <h3 className="text-xl font-extrabold text-card-foreground mt-0.5">
                Work Package Feedback
              </h3>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-content-muted">
              <span className="w-2 h-2 bg-primary" />
              Score
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full flex items-center justify-center">
            <p className="text-content-muted font-medium text-center px-4 text-sm">
              Loading work package data...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // One bar per actual assignment, ordered chronologically. Work-package
  // numbers are the global 1..N sequence — no fixed 8-bar / 4x2 assumption.
  const orderedAssignments = [...assignments].sort(
    (a, b) =>
      new Date(a.start_date).getTime() - new Date(b.start_date).getTime() ||
      new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
  );

  const bars: BarData[] = orderedAssignments.map((assignment, index) => {
    const reportNumber = index + 1;

    let state: BarState = "placeholder";
    let score: number | null = null;
    const submission = submissions.find((s) => s.assignment_id === assignment.id);
    const review = submission ? reviews.find((r) => r.submission_id === submission.id) : null;
    const isClosed = assignment.time_status === "closed";
    const isFinal = review?.status === "admin_confirmed" || review?.status === "admin_updated";

    // A bar only gets a number once the review is signed off; submitted
    // work stays hatched "in review" until then.
    if (isFinal) {
      state = "scored";
      score = review?.final_score ?? 0;
    } else if (submission) {
      state = "in_review";
    } else if (isClosed) {
      state = "scored";
      score = 0;
    }

    return {
      label: `WP${reportNumber}`,
      state,
      score,
      firstScore: null,
      placeholderHeight: PLACEHOLDER_HEIGHTS[index % PLACEHOLDER_HEIGHTS.length],
    };
  });

  // Any bar with real activity (a score or an in-review submission) dismisses
  // the getting-started overlay.
  const hasScores = bars.some((b) => b.state !== "placeholder");

  // The "special" first assignment is the earliest window, not a hard-coded
  // week-1 / assignment-1 slot.
  const assignment1 = orderedAssignments[0];
  const assignment1Deadline = assignment1?.deadline ? formatDeadline(assignment1.deadline) : null;

  return (
    <Card className="relative overflow-hidden">
      {/* Green accent top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand to-green-700/0" />
      <CardHeader className="pb-0">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[10px] font-bold text-content-muted uppercase tracking-[2px]">
              Feedback
            </p>
            <h3 className="text-xl font-extrabold text-card-foreground mt-0.5">
              Work Package Feedback
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-semibold text-content-muted">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-brand" />
              First submission
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#9FDDBC]" />
              Revision uplift
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#B0563C]" />
              Revision drop
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-[3px]"
                style={{
                  background:
                    "repeating-linear-gradient(45deg, rgba(30,111,79,.35) 0 3px, rgba(30,111,79,.08) 3px 6px)",
                }}
              />
              In review
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative">
        <div className="relative mt-1 h-[310px]">
          {/* Grid lines with y-axis labels */}
          <div className="absolute inset-0 top-6 bottom-[26px] left-9 flex flex-col justify-between">
            {[100, 75, 50, 25, 0].map((tick) => (
              <div
                key={tick}
                className={`relative border-t ${tick === 0 ? "border-solid border-content-border" : "border-dashed border-content-border/70"}`}
              >
                <span className="absolute -left-9 -top-[7px] text-[11px] font-bold text-content-muted/70">
                  {tick}
                </span>
              </div>
            ))}
          </div>
          {/* Bars */}
          <div className="absolute inset-0 left-9 flex items-stretch gap-3.5 px-2.5">
            {bars.map((bar) => (
              <ScoreBar key={bar.label} bar={bar} />
            ))}
          </div>
        </div>
        {!hasScores && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: "linear-gradient(180deg, hsl(var(--card) / .5), hsl(var(--card) / .88))",
            }}
          >
            <div className="w-[410px] max-w-[92%] max-h-full overflow-y-auto rounded-xl border border-content-border bg-card p-5 px-6 shadow-[0_8px_28px_rgba(1,50,32,.09)]">
              <h3 className="text-[15px] font-bold text-card-foreground">Get off the mark</h3>
              <p className="mt-0.5 font-serif text-[13px] italic text-content-muted">
                Your feedback will appear here after your first review
              </p>
              <ol className="mt-3.5 flex flex-col gap-2">
                <li>
                  <Link
                    to="/how-to-get-started"
                    className="flex items-center gap-[11px] rounded-[9px] border border-content-border bg-content-subtle px-3 py-2.5 text-[13px] font-semibold text-content-foreground transition-colors hover:border-brand"
                  >
                    <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                      1
                    </span>
                    Read how the programme works
                  </Link>
                </li>
                <li>
                  <Link
                    to="/marking-guide"
                    className="flex items-center gap-[11px] rounded-[9px] border border-content-border bg-content-subtle px-3 py-2.5 text-[13px] font-semibold text-content-foreground transition-colors hover:border-brand"
                  >
                    <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
                      2
                    </span>
                    Read how to get the best feedback
                  </Link>
                </li>
                {assignment1 && (
                  <li>
                    <Link
                      to={`/submissions/${assignment1.id}`}
                      className="flex items-center gap-[11px] rounded-[9px] border border-brand bg-card px-3 py-2.5 text-[13px] font-bold text-[#1E6F4F] transition-colors hover:bg-brand/5"
                    >
                      <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-brand text-[11px] font-bold text-white">
                        3
                      </span>
                      <span>
                        Start Work Package 1: an unweighted practice run
                        {assignment1Deadline && `, due ${assignment1Deadline}`}
                      </span>
                    </Link>
                  </li>
                )}
              </ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
